import { sha256 } from "@noble/hashes/sha2.js";
import { createProtectedKey, IDENTITY_SALT } from './key-derivation';
import { base64URLStringToBuffer, bufferToBase64URLString } from './utils';
export const getPasskeyPrfSalt = () => {
    const input = new TextEncoder().encode(`train-passkey-prf-salt-v1:${IDENTITY_SALT}`);
    return new Uint8Array(sha256(input));
};
export const checkPrfSupport = async () => {
    const result = {
        supported: false,
        platformAuthenticatorAvailable: false,
        prfCapabilityReported: null,
    };
    if (typeof window === 'undefined' || !window.isSecureContext || !window.PublicKeyCredential) {
        result.reason = 'WebAuthn not available';
        return result;
    }
    try {
        result.platformAuthenticatorAvailable =
            await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable() ?? false;
    }
    catch { /* ignore */ }
    try {
        const capabilities = await PublicKeyCredential.getClientCapabilities?.();
        if (capabilities && 'prf' in capabilities) {
            result.prfCapabilityReported = capabilities.prf === true;
        }
    }
    catch { /* API not available */ }
    if (result.prfCapabilityReported === true) {
        result.supported = true;
        return result;
    }
    if (result.prfCapabilityReported === null && result.platformAuthenticatorAvailable) {
        result.supported = true;
        result.reason = 'PRF support could not be confirmed; will attempt at auth time';
        return result;
    }
    if (!result.platformAuthenticatorAvailable) {
        result.reason = 'No platform authenticator available';
        result.platformHint = 'unsupported_browser';
    }
    else {
        result.reason = 'PRF not supported by this browser';
    }
    return result;
};
export const registerPasskey = async (forceCreate, displayName, storage) => {
    if (!forceCreate && storage) {
        const existing = await storage.getActiveCredentialId();
        if (existing)
            return { credentialId: existing };
    }
    if (typeof window === 'undefined')
        throw new Error('Passkey registration must run in a browser');
    if (!window.isSecureContext)
        throw new Error('Passkeys require HTTPS (secure context)');
    const challengeBytes = new Uint8Array(32);
    window.crypto.getRandomValues(challengeBytes);
    const userIdBytes = new Uint8Array(16);
    window.crypto.getRandomValues(userIdBytes);
    const prfSalt = getPasskeyPrfSalt();
    const existingIds = (await storage?.getAllCredentialIds()) ?? [];
    const excludeCredentials = existingIds.map(id => ({
        type: 'public-key',
        id: base64URLStringToBuffer(id),
    }));
    const publicKey = {
        challenge: challengeBytes,
        rp: { name: 'Train', id: window.location.hostname },
        user: {
            id: userIdBytes,
            name: 'train-user',
            displayName: displayName?.trim() || 'Train user',
        },
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
        excludeCredentials,
        authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
        attestation: 'none',
        timeout: 60000,
        extensions: { prf: { eval: { first: prfSalt } } },
    };
    const credential = await navigator.credentials.create({ publicKey });
    if (!credential)
        throw new Error('Failed to create passkey credential');
    const credentialId = bufferToBase64URLString(credential.rawId);
    await storage?.storeCredentialId(credentialId);
    const ext = credential.getClientExtensionResults?.() ?? {};
    const prfFirst = ext?.prf?.results?.first;
    if (prfFirst) {
        const ikm = new Uint8Array(prfFirst);
        const key = await createProtectedKey(ikm);
        ikm.fill(0);
        return { credentialId, key };
    }
    return { credentialId };
};
export const deriveKeyWithPasskey = async (options, storage) => {
    const createIfMissing = options?.createIfMissing !== false;
    if (typeof window === 'undefined')
        throw new Error('Passkey auth must run in a browser');
    if (!window.isSecureContext)
        throw new Error('Passkeys require HTTPS (secure context)');
    const prfSalt = getPasskeyPrfSalt();
    const challengeBytes = new Uint8Array(32);
    window.crypto.getRandomValues(challengeBytes);
    const publicKey = {
        rpId: window.location.hostname,
        challenge: challengeBytes,
        userVerification: 'required',
        extensions: { prf: { eval: { first: prfSalt } } },
    };
    let cred = (await navigator.credentials.get({ publicKey }));
    if (!cred) {
        if (!createIfMissing)
            throw new Error('No passkey found for this site. Create one instead.');
        const result = await registerPasskey(true, undefined, storage);
        if (result.key)
            return { key: result.key, credentialId: result.credentialId };
        cred = (await navigator.credentials.get({ publicKey }));
        if (!cred)
            throw new Error('Passkey authentication was cancelled or no passkey is available');
    }
    const credentialId = bufferToBase64URLString(cred.rawId);
    const ext = cred.getClientExtensionResults?.() ?? {};
    const prfFirst = ext?.prf?.results?.first;
    if (!prfFirst)
        throw new Error('Passkey PRF extension not available in this browser/authenticator');
    const ikm = new Uint8Array(prfFirst);
    const key = await createProtectedKey(ikm);
    ikm.fill(0);
    return { key, credentialId };
};
